// The on-demand tool endpoints of the worker API: token verification, the deployed hook's fee quote and
// the BellHook swap fee log. They sit under the same /api/data/ prefix (and Vercel rewrite) as the rest of
// the worker API. Contracts (docs/worker.md in the product repository):
//   GET /api/data/verify/:address?chainId=4663
//     -> { address, chainId, official, reason, checks: { codehash, beacon, create2 }, verifier, blockNumber }
//   GET /api/data/quote?chainId=&pool=&amountIn=&zeroForOne=
//     -> { feeBps, feePips, stale, gapBps, components?, reference?, source }
//   GET /api/data/hook/fees?chainId=
//     -> { rows: [{ txHash, poolId, timestamp, quotedFeePips, swapFeePips, amountIn, amountOut }], summary? }
// 400 means the request was refused (bad address, unknown pool); 502 means the worker could not reach the
// chain. Neither is retried here: the reader asked once and sees the answer.
import { ApiError, base, isOff, offError } from './client';

export const TOOL_MS = 10000;

/** true passed, false failed, null informational only (the worker returned ok: null). */
export type CheckState = boolean | null;

export interface VerifyChecks {
  codehash: CheckState;
  beacon: CheckState;
  create2: CheckState;
}

export interface VerifyPayload {
  address: string;
  chainId: number;
  official: boolean;
  reason: string;
  checks: VerifyChecks;
  verifier: string;
  blockNumber: number | string;
}

export interface QuotePayload {
  feeBps: number;
  feePips: number;
  stale: boolean;
  gapBps: number | null;
  components?: Record<string, unknown>;
  reference?: unknown;
  source: string;
}

export interface HookFeeRow {
  txHash: string;
  poolId: string;
  /** Unix seconds, or an ISO string. */
  timestamp: number | string;
  quotedFeePips: number;
  swapFeePips: number;
  amountIn: string | number;
  amountOut: string | number;
}

export interface HookFeesPayload {
  chainId?: number;
  rows: HookFeeRow[];
  summary?: unknown;
  /** false while the worker's indexer has not built the fee event table; rows are then empty. */
  indexed?: boolean;
}

/** A refused or failed tool call, with the worker's own message when it sent one. */
export class ToolError extends ApiError {
  constructor(message: string, status: number | null, timeout = false) {
    super(message, status, timeout);
    this.name = 'ToolError';
  }
}

type FetchFn = (input: string, init?: RequestInit) => Promise<Response>;

async function bodyMessage(r: Response): Promise<string | null> {
  try {
    const j = (await r.json()) as unknown;
    if (j && typeof j === 'object') {
      const o = j as Record<string, unknown>;
      for (const k of ['error', 'message', 'reason']) if (typeof o[k] === 'string' && o[k]) return o[k];
    }
  } catch {
    /* no JSON body */
  }
  return null;
}

/** GET base()+path once, as JSON, with a timeout. Non-2xx answers throw a ToolError carrying the status. */
export async function toolGet(path: string, fetchFn: FetchFn = fetch, ms: number = TOOL_MS): Promise<unknown> {
  if (isOff()) throw offError();
  const ctl = new AbortController();
  const timer = setTimeout(() => { ctl.abort(); }, ms);
  try {
    const r = await fetchFn(base() + path, { signal: ctl.signal, headers: { accept: 'application/json' } });
    if (!r.ok) throw new ToolError((await bodyMessage(r)) ?? `HTTP ${r.status}`, r.status);
    return (await r.json()) as unknown;
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') throw new ToolError(`no answer within ${ms / 1000} s`, null, true);
    throw e;
  } finally {
    clearTimeout(timer);
  }
}

/** The reader-facing text for a failed tool call. */
export function toolErrorText(e: unknown): string {
  if (e instanceof ApiError && e.off) return 'The live API is switched off for this page (?api=off).';
  if (e instanceof ApiError && e.timeout) return `The API did not answer in time (${e.message}).`;
  if (e instanceof ApiError && e.status === 400) return `The API refused the request: ${e.message === 'HTTP 400' ? 'invalid input' : e.message}.`;
  if (e instanceof ApiError && e.status === 502) return `The API could not read the chain right now (502${e.message === 'HTTP 502' ? '' : `: ${e.message}`}). Try again in a minute.`;
  if (e instanceof ApiError && e.status !== null) return `The API answered HTTP ${e.status}${e.message.startsWith('HTTP') ? '' : `: ${e.message}`}.`;
  if (e instanceof Error && e.message.startsWith('malformed')) return `The API answer did not match the expected shape (${e.message}).`;
  return 'The API could not be reached.';
}

/* ---------- paths ---------- */

export const verifyPath = (address: string, chainId: number): string =>
  `/api/data/verify/${encodeURIComponent(address)}?chainId=${chainId}`;

export interface QuoteParams {
  chainId: number;
  pool: string;
  /** Raw integer amount in the input token's smallest unit. */
  amountIn: string;
  zeroForOne: boolean;
}

export const quotePath = (q: QuoteParams): string => {
  const p = new URLSearchParams({ chainId: String(q.chainId), pool: q.pool, amountIn: q.amountIn, zeroForOne: String(q.zeroForOne) });
  return `/api/data/quote?${p.toString()}`;
};

export const hookFeesPath = (chainId: number): string => `/api/data/hook/fees?chainId=${chainId}`;

/* ---------- shape checks ---------- */

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const numLike = (v: unknown): number | null => {
  if (isNum(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
};

/** A check from /verify: a bare boolean, or an object like { ok: true, actual, expected }. Anything else fails. */
const checkState = (x: unknown): CheckState => {
  if (x === true) return true;
  if (isObj(x)) return x.ok === true ? true : x.ok === null ? null : false;
  return false;
};

export function asVerify(v: unknown): VerifyPayload {
  if (!isObj(v) || typeof v.official !== 'boolean' || typeof v.address !== 'string') throw new Error('malformed /verify answer');
  const c = isObj(v.checks) ? v.checks : {};
  return {
    address: v.address,
    chainId: numLike(v.chainId) ?? 0,
    official: v.official,
    reason: typeof v.reason === 'string' ? v.reason : '',
    checks: { codehash: checkState(c.codehash), beacon: checkState(c.beacon), create2: checkState(c.create2) },
    verifier: typeof v.verifier === 'string' ? v.verifier : '',
    blockNumber: typeof v.blockNumber === 'number' || typeof v.blockNumber === 'string' ? v.blockNumber : '',
  };
}

export function asQuote(v: unknown): QuotePayload {
  if (!isObj(v)) throw new Error('malformed /quote answer');
  const feePips = numLike(v.feePips);
  const feeBps = numLike(v.feeBps) ?? (feePips === null ? null : feePips / 100);
  if (feeBps === null || typeof v.stale !== 'boolean') throw new Error('malformed /quote answer');
  const out: QuotePayload = {
    feeBps,
    feePips: feePips ?? Math.round(feeBps * 100),
    stale: v.stale,
    gapBps: numLike(v.gapBps),
    source: typeof v.source === 'string' ? v.source : '',
  };
  if (isObj(v.components)) out.components = v.components;
  if (v.reference !== undefined) out.reference = v.reference;
  return out;
}

export function asHookFees(v: unknown): HookFeesPayload {
  if (!isObj(v) || !Array.isArray(v.rows)) throw new Error('malformed /hook/fees answer');
  const rows: HookFeeRow[] = [];
  for (const r of v.rows as unknown[]) {
    if (!isObj(r) || typeof r.txHash !== 'string' || typeof r.poolId !== 'string') continue;
    rows.push({
      txHash: r.txHash,
      poolId: r.poolId,
      timestamp: typeof r.timestamp === 'number' || typeof r.timestamp === 'string' ? r.timestamp : 0,
      quotedFeePips: numLike(r.quotedFeePips) ?? NaN,
      swapFeePips: numLike(r.swapFeePips) ?? NaN,
      amountIn: typeof r.amountIn === 'string' || typeof r.amountIn === 'number' ? r.amountIn : '',
      amountOut: typeof r.amountOut === 'string' || typeof r.amountOut === 'number' ? r.amountOut : '',
    });
  }
  const out: HookFeesPayload = { rows, summary: v.summary };
  if (typeof v.indexed === 'boolean') out.indexed = v.indexed;
  const chainId = numLike(v.chainId);
  if (chainId !== null) out.chainId = chainId;
  return out;
}

/* ---------- calls ---------- */

export const fetchVerify = async (address: string, chainId: number, f?: FetchFn): Promise<VerifyPayload> =>
  asVerify(await toolGet(verifyPath(address, chainId), f));
export const fetchQuote = async (q: QuoteParams, f?: FetchFn): Promise<QuotePayload> =>
  asQuote(await toolGet(quotePath(q), f));
export const fetchHookFees = async (chainId: number, f?: FetchFn): Promise<HookFeesPayload> =>
  asHookFees(await toolGet(hookFeesPath(chainId), f));

/* ---------- formatting ---------- */

/** Uniswap v4 fee units: 1 pip = 1/100 bps (1,000,000 pips = 100 percent). */
export const pipsToBps = (pips: number): number => pips / 100;

export function fmtBps(bps: number): string {
  if (!Number.isFinite(bps)) return 'n/a';
  return `${Number.isInteger(bps) ? bps.toFixed(0) : bps.toFixed(2).replace(/0$/, '')} bps`;
}

/** Decimal amount ("1.5") to a raw integer string for `decimals`; null when not a positive decimal. */
export function toRawAmount(text: string, decimals: number): string | null {
  const t = text.trim();
  if (!/^\d+(\.\d+)?$/.test(t)) return null;
  const [whole = '0', frac = ''] = t.split('.');
  if (frac.length > decimals) return null;
  const raw = (whole + frac.padEnd(decimals, '0')).replace(/^0+(?=\d)/, '');
  return /^0+$/.test(raw) ? null : raw;
}

export function tsToDate(ts: number | string): Date | null {
  if (typeof ts === 'number') return Number.isFinite(ts) && ts > 0 ? new Date(ts < 1e12 ? ts * 1000 : ts) : null;
  const n = Number(ts);
  if (ts.trim() !== '' && Number.isFinite(n)) return tsToDate(n);
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d;
}

export interface PoolFeeSummary {
  poolId: string;
  swaps: number;
  /** Median fee charged, in bps. */
  medianSwapBps: number | null;
  maxSwapBps: number | null;
  /** Swaps where the fee charged differs from the fee quoted before the swap. */
  mismatches: number;
}

const median = (xs: number[]): number | null => {
  if (xs.length === 0) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? (s[m] ?? null) : ((s[m - 1] ?? 0) + (s[m] ?? 0)) / 2;
};

/** Per-pool summary computed from the rows themselves, in first-seen pool order. */
export function summarizeFees(rows: readonly HookFeeRow[]): PoolFeeSummary[] {
  const by = new Map<string, HookFeeRow[]>();
  for (const r of rows) {
    const k = r.poolId.toLowerCase();
    const list = by.get(k);
    if (list) list.push(r);
    else by.set(k, [r]);
  }
  return [...by.values()].map((list) => {
    const fees = list.map((r) => r.swapFeePips).filter(Number.isFinite).map(pipsToBps);
    return {
      poolId: list[0]?.poolId ?? '',
      swaps: list.length,
      medianSwapBps: median(fees),
      maxSwapBps: fees.length ? Math.max(...fees) : null,
      mismatches: list.filter((r) => Number.isFinite(r.quotedFeePips) && Number.isFinite(r.swapFeePips) && r.quotedFeePips !== r.swapFeePips).length,
    };
  });
}

export const shortHex = (h: string): string => (h.length > 14 ? `${h.slice(0, 8)}…${h.slice(-6)}` : h);
